/**
 * Employee Incident List Management Mapper
 * Maps transport layer DTOs to domain models for the admin-wide incident listing (ASIS-5244)
 */
import type {
  LC_ListIncidentsManagementOut,
  LC_IncidentManagementItemOut,
  IncidentManagementItem,
  IncidentManagementTotals,
  IncidentManagementPage,
} from '../types';
import { formatDateToDDMMYYYY } from './employeeIncidentCommon.mapper';

/**
 * Maps single incident item (admin-wide list management, ASIS-5244) from backend to UI model
 */
export function mapIncidentManagementItemOut(
  item: LC_IncidentManagementItemOut,
): IncidentManagementItem {
  return {
    incidentId: item.incidentId,
    status: item.status || '-',
    taxId: item.taxIdentificationNumber || '-',
    employeeName: item.employeeFullName || '-',
    errorType: item.errorType || '-',
    incidentDate: formatDateToDDMMYYYY(item.incidentDate),
    updatedDate: formatDateToDDMMYYYY(item.updatedDate),
    markingDate: formatDateToDDMMYYYY(item.markingDate),
    markingTime: item.markingTime || '-',
    eventName: item.eventName || '-',
    locationName: item.locationName || '-',
    approvedBy: item.approvedByFullName || '-',
    requesterComment: item.requesterComment || '-',
    approverComment: item.approverComment || '-',
  };
}

/**
 * Extracts status counters from the management list pagination metadata (ASIS-5243).
 * Calculados sobre todos los filtros activos excepto el propio filtro de estado.
 */
export function mapIncidentManagementTotals(
  pagination: LC_ListIncidentsManagementOut['pagination'],
): IncidentManagementTotals {
  return {
    pendientes: pagination.totalPending,
    aprobados: pagination.totalApproved,
    rechazados: pagination.totalRejected,
    error: pagination.totalError,
    todos: pagination.totalAll,
  };
}

/**
 * Maps complete list management response (ASIS-5244) from backend to UI model
 */
export function mapIncidentManagementListResponse(
  response: LC_ListIncidentsManagementOut,
): IncidentManagementPage {
  const items = response.data?.map(mapIncidentManagementItemOut) || [];
  return {
    items,
    totals: mapIncidentManagementTotals(response.pagination),
    totalRecords: response.pagination.totalRecords,
  };
}
